

export interface AgentConfigData {
  // Informations d'identification
  pontBasculeId: string;
  pontBasculeName: string;
  pontBasculeCode: string;
  tenantId: string;
  tenantName: string;
  tenantCode: string;
  siteId: string;
  siteName: string;
  siteCode: string;
  
  // Configuration WebSocket
  wsUrl: string;
  port: number;
  
  // Configuration de la balance
  scale: {
    unit: string;
    precision: number;
    stabilityThreshold: number;
  };
  
  // Configuration de reconnexion
  reconnect: {
    enabled: boolean;
    maxAttempts: number;
    delay: number;
  };
  
  // Configuration réseau
  network: {
    ipAddress: string;
    subnet: string;
    gateway: string;
    dns: string;
  };
  
  // Configuration de sécurité
  security: {
    apiKey: string;
    apiSecretKey: string; // Clé secrète pour authentifier les données envoyées par l'agent
    requireAuthentication: boolean;
    allowedOrigins: string[];
  };
  
  // Informations système
  system: {
    status: string;
    isDefault: boolean;
    location?: string;
    description?: string;
  };
  
  // Timestamps
  timestamps: {
    created_at?: string;
    updated_at?: string;
    config_generated_at: string;
  };
}

export interface AgentReturnData {
  networkInfo: {
    localIp: string;
    macAddress: string;
    hostname: string;
    osInfo: string;
  };
  connectionStatus: {
    websocketConnected: boolean;
    lastConnection: string;
    uptime: number;
  };
  scaleStatus: {
    connected: boolean;
    lastReading: number;
    unit: string;
    stability: boolean;
  };
  timestamp: string;
  agentVersion: string;
}

export interface AgentConfigFile {
  version: string;
  metadata: {
    generated_at: string;
    pont_bascule_id: string;
    site_id: number;
    tenant_id: number;
    installation_completed?: boolean;
  };
  configuration: {
    pontBasculeId: string;
    pontBasculeName: string;
    pontBasculeCode: string;
    tenantId: number;
    tenantName: string;
    tenantCode: string;
    siteId: number;
    siteName: string;
    siteCode: string;
    wsUrl: string;
    port: number;
    network: {
      ipAddress: string;
      macAddress: string;
      hostname: string;
    };
    security: {
      apiKey: string;
      requireAuthentication: boolean;
    };
    system: {
      status: string;
      platform: string;
    };
  };
  agentEndpoints: {
    health: string;
    weighRead: string;
    weighStream: string;
  };
  serviceInfo: {
    serviceName: string;
    status: string;
    port: number;
  };
}

export class ConfigFileGenerator {
  private static readonly VERSION = '1.0.0';

  /**
   * Prépare les données de configuration pour l'agent local
   */
  static prepareConfigData(pontBascule: any, tenant: any, site: any): AgentConfigData {
    return {
      // Informations d'identification
      pontBasculeId: pontBascule.id || 'N/A',
      pontBasculeName: pontBascule.name || 'N/A',
      pontBasculeCode: pontBascule.code || 'N/A',
      tenantId: tenant?.id || 'N/A',
      tenantName: tenant?.name || 'N/A',
      tenantCode: tenant?.code || 'N/A',
      siteId: site?.id || 'N/A',
      siteName: site?.name || 'N/A',
      siteCode: site?.code || 'N/A',
      
      // Configuration WebSocket
              wsUrl: pontBascule.wsUrl || pontBascule.scaleAgentConfig?.wsUrl || 'ws://127.0.0.1:9090/weigh/stream',
      port: pontBascule.port || pontBascule.scaleAgentConfig?.port || 9090,
      
      // Configuration de la balance
      scale: {
        unit: pontBascule.scaleUnit || pontBascule.scaleAgentConfig?.scale?.unit || 'kg',
        precision: pontBascule.scalePrecision || pontBascule.scaleAgentConfig?.scale?.precision || 2,
        stabilityThreshold: pontBascule.stabilityThreshold || pontBascule.scaleAgentConfig?.scale?.stabilityThreshold || 0.01,
      },
      
      // Configuration de reconnexion
      reconnect: {
        enabled: pontBascule.reconnectEnabled || pontBascule.scaleAgentConfig?.reconnect?.enabled || true,
        maxAttempts: pontBascule.reconnectMaxAttempts || pontBascule.scaleAgentConfig?.reconnect?.maxAttempts || 5,
        delay: pontBascule.reconnectDelay || pontBascule.scaleAgentConfig?.reconnect?.delay || 1000,
      },
      
      // Configuration réseau
      network: {
        ipAddress: pontBascule.networkIpAddress || pontBascule.networkConfig?.ipAddress || '127.0.0.1',
        subnet: pontBascule.networkSubnet || pontBascule.networkConfig?.subnet || '255.255.255.0',
        gateway: pontBascule.networkGateway || pontBascule.networkConfig?.gateway || '192.168.1.1',
        dns: pontBascule.networkDns || pontBascule.networkConfig?.dns || '8.8.8.8',
      },
      
      // Configuration de sécurité
      security: {
        apiKey: pontBascule.apiKey || pontBascule.securityConfig?.apiKey || '',
        apiSecretKey: pontBascule.apiSecretKey || pontBascule.securityConfig?.apiSecretKey || '', // Clé secrète pour authentifier les données envoyées par l'agent
        requireAuthentication: pontBascule.requireAuthentication || pontBascule.securityConfig?.requireAuthentication || false,
        allowedOrigins: pontBascule.allowedOrigins || pontBascule.securityConfig?.allowedOrigins || ['127.0.0.1', 'localhost'],
      },
      
      // Informations système
      system: {
        status: pontBascule.status || 'active',
        isDefault: pontBascule.isDefault || false,
        location: pontBascule.location,
        description: pontBascule.description,
      },
      
      // Timestamps
      timestamps: {
        created_at: pontBascule.createdAt,
        updated_at: pontBascule.updatedAt,
        config_generated_at: new Date().toISOString(),
      }
    };
  }

  /**
   * Génère le contenu du fichier de configuration JSON en clair
   */
  static generateConfigFileContent(config: AgentConfigData): string {
    // Créer le contenu du fichier JSON en clair
    const fileContent = {
      version: this.VERSION,
      metadata: {
        generated_at: new Date().toISOString(),
        pont_bascule_id: config.pontBasculeId,
        site_id: config.siteId,
        tenant_id: config.tenantId
      },
      configuration: config,
      instructions: {
        usage: "1. Copier ce fichier dans le répertoire de l'agent local",
        note: "Ce fichier contient toutes les informations nécessaires pour configurer l'agent local"
      }
    };
    
    return JSON.stringify(fileContent, null, 2);
  }

  /**
   * Télécharge le fichier de configuration JSON en clair
   */
  static downloadConfigFile(config: AgentConfigData, filename: string): void {
    try {
      const content = this.generateConfigFileContent(config);
      const blob = new Blob([content], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      
      const a = document.createElement('a');
      a.href = url;
      a.download = filename.replace('.txt', '.json');
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      console.log('✅ Fichier JSON de configuration téléchargé:', filename.replace('.txt', '.json'));
      
    } catch (error) {
      console.error('❌ Erreur lors de la génération du fichier JSON:', error);
      throw error;
    }
  }

  /**
   * Parse le fichier de retour de l'agent local (JSON en clair)
   */
  static parseReturnFile(content: string): AgentReturnData | null {
    try {
      const data = JSON.parse(content);
      
      // Validation détaillée des données
      const missingFields: string[] = [];
      
      if (!data.networkInfo) {
        missingFields.push('networkInfo');
      } else {
        if (!data.networkInfo.localIp) missingFields.push('networkInfo.localIp');
        if (!data.networkInfo.macAddress) missingFields.push('networkInfo.macAddress');
        if (!data.networkInfo.hostname) missingFields.push('networkInfo.hostname');
        if (!data.networkInfo.osInfo) missingFields.push('networkInfo.osInfo');
      }
      
      if (!data.connectionStatus) {
        missingFields.push('connectionStatus');
      } else {
        if (typeof data.connectionStatus.websocketConnected !== 'boolean') missingFields.push('connectionStatus.websocketConnected (boolean)');
        if (!data.connectionStatus.lastConnection) missingFields.push('connectionStatus.lastConnection');
        if (typeof data.connectionStatus.uptime !== 'number') missingFields.push('connectionStatus.uptime (number)');
      }
      
      if (!data.scaleStatus) {
        missingFields.push('scaleStatus');
      } else {
        if (typeof data.scaleStatus.connected !== 'boolean') missingFields.push('scaleStatus.connected (boolean)');
        if (typeof data.scaleStatus.lastReading !== 'number') missingFields.push('scaleStatus.lastReading (number)');
        if (!data.scaleStatus.unit) missingFields.push('scaleStatus.unit');
        if (typeof data.scaleStatus.stability !== 'boolean') missingFields.push('scaleStatus.stability (boolean)');
      }
      
      if (!data.timestamp) missingFields.push('timestamp');
      if (!data.agentVersion) missingFields.push('agentVersion');
      
      if (missingFields.length > 0) {
        throw new Error(`Format de fichier invalide - champs manquants ou incorrects: ${missingFields.join(', ')}`);
      }
      
      return data as AgentReturnData;
    } catch (error) {
      if (error instanceof SyntaxError) {
        throw new Error('Format de fichier invalide - JSON malformé');
      }
      throw error;
    }
  }

  /**
   * Parse le fichier de configuration de l'agent local
   */
  static parseConfigFile(content: string): AgentConfigFile | null {
    try {
      const data = JSON.parse(content);
      
      // Validation de la structure de base
      if (!data.version || !data.metadata || !data.configuration) {
        throw new Error('Format de fichier invalide - structure de base manquante (version, metadata, configuration)');
      }
      
      // Validation des métadonnées
      if (!data.metadata.generated_at || !data.metadata.pont_bascule_id || 
          typeof data.metadata.site_id !== 'number' || typeof data.metadata.tenant_id !== 'number') {
        throw new Error('Format de fichier invalide - métadonnées incomplètes ou incorrectes');
      }
      
      // Validation de la configuration
      if (!data.configuration.pontBasculeId || !data.configuration.pontBasculeName || 
          !data.configuration.tenantId || !data.configuration.siteId) {
        throw new Error('Format de fichier invalide - configuration incomplète');
      }
      
      // Validation des endpoints (optionnels mais recommandés)
      if (data.agentEndpoints) {
        if (!data.agentEndpoints.health || !data.agentEndpoints.weighRead || !data.agentEndpoints.weighStream) {
          console.warn('⚠️ Endpoints d\'agent incomplets détectés');
        }
      }
      
      return data as AgentConfigFile;
    } catch (error) {
      if (error instanceof SyntaxError) {
        throw new Error('Format de fichier invalide - JSON malformé');
      }
      throw error;
    }
  }

  /**
   * Détecte automatiquement le type de fichier et le parse
   */
  static parseAgentFile(content: string): AgentReturnData | AgentConfigFile | null {
    try {
      const data = JSON.parse(content);
      
      // Détecter le type de fichier basé sur la structure
      if (data.networkInfo && data.connectionStatus && data.scaleStatus) {
        // C'est un fichier de retour d'état
        return this.parseReturnFile(content);
      } else if (data.version && data.metadata && data.configuration) {
        // C'est un fichier de configuration
        return this.parseConfigFile(content);
      } else {
        throw new Error('Format de fichier non reconnu - ni fichier de retour ni fichier de configuration');
      }
    } catch (error) {
      if (error instanceof SyntaxError) {
        throw new Error('Format de fichier invalide - JSON malformé');
      }
      throw error;
    }
  }

  /**
   * Génère un exemple de fichier de retour valide pour l'agent local
   */
  static generateExampleReturnFile(): string {
    const exampleData: AgentReturnData = {
      networkInfo: {
        localIp: '192.168.1.100',
        macAddress: '00:11:22:33:44:55',
        hostname: 'AGENT-PC-001',
        osInfo: 'Windows 10 Pro 21H2'
      },
      connectionStatus: {
        websocketConnected: true,
        lastConnection: new Date().toISOString(),
        uptime: 3600
      },
      scaleStatus: {
        connected: true,
        lastReading: 1250.5,
        unit: 'kg',
        stability: true
      },
      timestamp: new Date().toISOString(),
      agentVersion: '1.0.0'
    };
    
    return JSON.stringify(exampleData, null, 2);
  }
}

export default ConfigFileGenerator;
